/*----------------------------------
- COMPONENT: LAZY PUBLIC DOMAIN ROW DRAWER
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @adr  ADR-0028
 * @rule The drawer chunk is requested only once a row is opened. It carries framer-motion, so a
 *       static import here would put it in Home's eager chunk. Loading, retry and the exit's last
 *       row are the shared lazy preview drawer's (`useLazyPreviewDrawer`).
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { createPreviewDrawerLoader, useLazyPreviewDrawer } from './useLazyPreviewDrawer';

// Types
import type { PublicDomainsTableRow } from '../table/PublicDomainsTable';
import type { PublicDomainRowDrawerProps } from './PublicDomainRowDrawer';

/*----------------------------------
- TYPES
----------------------------------*/

type LazyPublicDomainRowDrawerProps = Omit<PublicDomainRowDrawerProps, 'row' | 'open'> & {
    /** The row being previewed, or null while the drawer is closed. */
    row: PublicDomainsTableRow | null;
};

/*----------------------------------
- HELPERS
----------------------------------*/

const drawerLoader = createPreviewDrawerLoader<PublicDomainRowDrawerProps>(() => import('./PublicDomainRowDrawer'));

/*----------------------------------
- COMPONENT
----------------------------------*/

const LazyPublicDomainRowDrawer = ({ row, ...props }: LazyPublicDomainRowDrawerProps) => {
    const { Drawer, drawn: drawnRow, isOpen } = useLazyPreviewDrawer(drawerLoader, row, props.onRequestClose);
    if (!drawnRow || !Drawer) return null;
    return <Drawer row={drawnRow} open={isOpen} {...props} />;
};

export default LazyPublicDomainRowDrawer;
